/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import {
    AclModel,
    CloudianLayer7Template,
    ClusterService,
    FIPApiService,
    FipModel,
    GSLBTemplate,
    HangingBackendsModalComponent,
    HangingRealServersModalComponent,
    L7BackendServiceModel,
    L7VirtualServiceModel,
    Layer47WizardService,
    Layer4ApiService,
    Layer4Template,
    Layer7ApiService,
    Layer7Template,
    LockModel,
    LockService,
    MonitorTemplate,
    QuickService,
    ServicesUtilsService,
    StatusEnum,
    TemplateModel
} from '../../../shared';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { BaseComponent } from '../../../../../core/components';
import { Component, ComponentRef, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { ErrorResponseModel, ErrorsResponseModel, NotificationModel } from '../../../../../core/models';
import {
    LanguageService,
    LoadingService,
    LogService,
    ModalService,
    NavigationService,
    NotifyService,
    RbacService,
    TransactionService,
    UtilsService
} from '../../../../../core/services';
import { TableMultiSelectModel } from '../../../../../core/models/tableMultiSelect.model';
import { WidgetService } from '../../../shared/services/utilities/widget.service';
import { catchError, concatMap, mergeMap, takeUntil } from 'rxjs/operators';
import { forkJoin, from, Observable, of, Subscriber } from 'rxjs';

@Component({
    selector: 'app-manage-services',
    templateUrl: './manageServices.component.html',
    styleUrls: ['./manageServices.component.scss']
})

export class ManageServicesComponent extends BaseComponent implements OnInit {
    /**
     * Reference to new service container.
     */
    @ViewChild('modalContainer', { read: ViewContainerRef, static: true })
    public modalContainer: any;

    /**
     * To store the table row data.
     */
    public tableRows: any[] = [];

    /**
     * Search term for searching the services table, also used with the search query parameter in the url.
     */
    public searchTerm = '';

    /**
     * To store the list of real servers, array index is backend id.
     */
    public realServersByBackend: any[] = [];

    /**
     * To store the list of hanging realservers.
     */
    public hangingReals: any[] = [];

    /**
     * To store the list of hanging backends.
     */
    public hangingBackends: any[] = [];

    /**
     * To store state of all the forms.
     */
    public controllerState: any = [];

    /**
     * Our acl rules.
     */
    public aclRules: AclModel[] = [];

    /**
     * Modal Component Reference.
     */
    public modalComponentReference: ComponentRef<any>;

    /**
     * An array to store all selected row information.
     */
    public selectedRows: any[] = [];

    /**
     * External: Call back function in the parent to update the backends on the parent.
     */
    public backendToDelete: (...args: any[]) => void;

    /**
     * Stored transaction state
     */
    public transactionActive = false;

    /**
     * Flag for appliance version mismatch in cluster.
     */
    public versionMismatch = false;

    /**
     * Store button text and action for select all action dots.
     */
    public multiSelectActions: TableMultiSelectModel[] = [];

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * To store the progress message.
     */
    public progressMessage = '';

    /**
     * Our available made frontends from the API.
     * @private
     */
    private frontends: L7VirtualServiceModel[] = [];

    /**
     * Our available made frontends from the API.
     * @private
     */
    private backends: L7BackendServiceModel[] = [];

    /**
     * Our available floating IPs.
     * @private
     */
    private floatingIPs: FipModel[] = [];

    /**
     * Like a loading state for socket data.
     * @private
     */
    private firstPassSocketData = true;

    /**
     * Store the selected rows from the table output.
     * @private
     */
    private selectedTableRows: any [] = [];

    /**
     * Store frontend status.
     * @private
     */
    private storedFrontendStatus: any = {};

    constructor(
        public servicesUtilsService: ServicesUtilsService,
        private activatedRoute: ActivatedRoute,
        private clusterService: ClusterService,
        private fipApiService: FIPApiService,
        private languageService: LanguageService,
        private layer47WizardService: Layer47WizardService,
        private layer4ApiService: Layer4ApiService,
        private layer7ApiService: Layer7ApiService,
        private loadingService: LoadingService,
        private lockService: LockService,
        private logService: LogService,
        private modalService: ModalService,
        private navigationService: NavigationService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private router: Router,
        private transactionService: TransactionService,
        private utilsService: UtilsService,
        private widgetService: WidgetService
    ) {
        super();

        this.setUpPageData();
        this.setupSubscribers();
        this.setUpModalSubscriber();
        this.setupSocketListeners();
        this.registerButtons();
        this.setSelectAllActions();
    }

    /**
     * Get status enum.
     */
    get statusEnum(): any {
        return StatusEnum;
    }

    /**
     * Updated the ports to show the range ports with a '-'.
     * @param ports
     * @private
     */
    private static formatPortsForTable(ports: any): any {
        const updatedPorts: any[] = [];
        for (const port of ports) {
            if (port.length > 1) {
                updatedPorts.push(port.join('-'));
            } else {
                updatedPorts.push(port);
            }
        }

        return updatedPorts;
    }

    public ngOnInit(): void {
        this.transactionActive = this.lockService.getLockStateByProperty('transactionLocked');
        this.versionMismatch = this.clusterService.hasNodeVersionMismatch();
    }

    /**
     * Form function triggered when a table row is selected.
     * @param event
     */
    public onSelect(event: any): void {
        this.selectedTableRows = event;
    }

    /**
     * Open the hanging backends modal.
     */
    public openHangingRealsModal(): void {
        const component: any = this.modalContainer.createComponent(HangingRealServersModalComponent);
        // Set the template in the wizard service.
        component.instance.hangingRealServerIds = this.hangingReals;
        component.instance.updateCallback = (): void => {
            this.setUpPageData();
        };

        // Assign component reference.
        this.modalComponentReference = component;
    }

    /**
     * Open the hanging backends modal.
     */
    public openHangingBackendsModal(): void {
        const hangingBackendsModal: any = this.modalContainer.createComponent(HangingBackendsModalComponent);

        // Set the template in the wizard service.
        hangingBackendsModal.instance.hangingBackends = this.hangingBackends;
        hangingBackendsModal.instance.realServersByBackend = this.realServersByBackend;
        hangingBackendsModal.instance.backendToDelete = (): void => {
            this.setUpPageData();
        };

        // Assign component reference.
        this.modalComponentReference = hangingBackendsModal;
    }

    /**
     * Navigate to the new service page.
     */
    public createNewService(): void {
        const services: any = { frontends: this.frontends, acls: this.aclRules, fips: this.floatingIPs };
        this.layer47WizardService.setServices(services);

        const navigationExtras: NavigationExtras = {
            state: {
                cancelText: 'manage_services_return',
                parentUrl: '/cluster-configuration/manage-services',
                editMode: false,
                duplicate: false,
                templates: this.getFormattedTemplates()
            }
        };

        this.router.navigateByUrl(this.navigationService.getEnduranceUrl('/service/add'), navigationExtras).then().catch();
    }

    /**
     * Get formatted templates.
     * @private
     */
    private getFormattedTemplates(): TemplateModel[] {
        const editedMonitorTemplate: TemplateModel = this.layer47WizardService.setMonitorTemplateState(
            MonitorTemplate, this.backends, this.hangingBackends
        );

        return [Layer4Template, Layer7Template, GSLBTemplate, editedMonitorTemplate, CloudianLayer7Template, QuickService];
    }

    /**
     * Takes in the action/api call you want to make and displays a confirmation modal and makes the call on submit
     * @param action
     * @param row
     */
    public frontendAction(action: string, row: any): void {
        const options: object = {
            onCompleteCallback: this.frontendServiceActionHelper.bind(this, action),
            logMessageTitle: 'ManageServices: frontendAction()',
            templateType: row.templateType
        };

        this.servicesUtilsService.actionService(action, row, options);
    }

    /**
     * Callback helper function for frontends.
     * @param action
     * @private
     */
    private frontendServiceActionHelper(action: string): void {
        const { title } = this.servicesUtilsService.getActionTypeAndTitle(action);

        if (title === 'delete') {
            this.setUpPageData();
        }
    }

    /**
     * Navigate to Layer 7 stick tables page.
     * @param row
     */
    public navigateToStickTables(row: { serviceName: any; id: any; }): void {
        const status: string = this.getFrontendStatus(row);
        if (status === 'online') {
            this.navigateToBackendStickTable(row.id);
        } else {
            this.notifyService.error('Unable to load stick table. Service must be online to view stick table');
        }
    }

    /**
     * Navigates to the edit service page to edit a frontend or monitor.
     * @param frontend
     * @param duplicate
     */
    public editService(frontend: any, duplicate?: boolean): void {
        const navigationExtras: NavigationExtras = {
            state: {
                cancelText: 'manage_services_return',
                duplicate: duplicate,
                editMode: true,
                parentUrl: '/cluster-configuration/manage-services',
                service: frontend,
                templateType: frontend.templateType,
                templates: this.getFrontendTemplate(frontend, duplicate),
                wizardTitle: this.languageService.translate(this.getWizardTitleFromType(frontend.templateType, duplicate))
            }
        };

        const url: string = duplicate ? '/service/add' : ('/service/edit/' + frontend.id);

        this.router.navigateByUrl(this.navigationService.getEnduranceUrl(url), navigationExtras).then().catch();
    }

    /**
     * Get wizard title depending on template.
     * @param templateType
     * @param duplicate
     * @private
     */
    private getWizardTitleFromType(templateType: string, duplicate?: boolean): string {
        let title: string;
        if (templateType === 'layer7') {
            title = duplicate ? 'manage_services_add_layer_7' : 'manage_services_edit_layer_7';
        } else if (templateType === 'layer4') {
            title = duplicate ? 'manage_services_add_layer_4' : 'manage_services_edit_layer_4';
        } else {
            title = duplicate ? 'manage_services_add_monitor' : 'manage_services_edit_monitor';
        }

        return title;
    }

    /**
     * Get template.
     * @param frontend
     * @param duplicate
     * @private
     */
    private getFrontendTemplate(frontend: any, duplicate?: boolean): TemplateModel {
        const availableTemplates: TemplateModel[] = this.getFormattedTemplates();
        const services: any = { frontends: this.frontends, acls: this.aclRules };
        this.layer47WizardService.setServices(services);
        const frontendId: string = frontend.id ? frontend.id : null;

        return this.layer47WizardService.setUpFrontendTemplate(
            frontendId, availableTemplates, frontend.templateType, duplicate
        );
    }

    /**
     * Retrieve our frontends, backends and acls via the API and update our local variable.
     * @private
     */
    private setUpPageData(): void {
        const apiCalls: any = {
            'frontends7': this.layer7ApiService.getFrontends(),
            'backends7': this.layer7ApiService.getBackends(),
            'realServer7': this.layer7ApiService.getRealServers(),
            'acls7': this.layer7ApiService.getAclRules(),
            'frontends4': this.layer4ApiService.getFrontends(),
            'backends4': this.layer4ApiService.getBackends(),
            'acls4': this.layer4ApiService.getAclRules(),
            'fips': this.fipApiService.getFloatingIPs(),
            'hangingL4Reals': this.layer4ApiService.getHangingRealServers()
        };

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    // Update frontend, backends and acls class variables.
                    if (data['frontends7']) {
                        this.utilsService.addTypeToService(data['frontends7'], 'layer7', 'Layer 7');
                    }

                    if (data['frontends4']) {
                        this.utilsService.addTypeToService(data['frontends4'], 'layer4', 'Layer 4');
                    }

                    if (data['backends7']) {
                        this.utilsService.addTypeToService(data['backends7'], 'layer7', 'Layer 7');
                    }

                    if (data['backends4']) {
                        this.utilsService.addTypeToService(data['backends4'], 'layer4', 'Layer 4');
                    }

                    if (data['hangingL4Reals']) {
                        const layer7RealServers: string[] = this.getLayer7HangingRealServerIds(
                            data['realServer7'], data['backends7']);
                        this.hangingReals = data['hangingL4Reals'].concat(layer7RealServers);
                    }

                    this.frontends = data['frontends7'].concat(data['frontends4']);
                    this.backends = data['backends7'].concat(data['backends4']);
                    this.aclRules = data['acls7'].concat(data['acls4']);
                    this.floatingIPs = data['fips'];

                    this.getHangingBackends();
                    this.populateTablesRows();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('ManageServicesComponent: setUpPageData', 'Failed to fetch data', error.message);
                }
            });
    }

    /**
     * Get layer 7 real service hanging ids. // TODO temp function while waiting for API to be created.
     * @param layer7RealServers
     * @param layer7Backends
     * @private
     */
    private getLayer7HangingRealServerIds(layer7RealServers: any[], layer7Backends: any[]): string[] {
        const hangingRealServerIds: string[] = [];
        const backendRipArray: any = layer7Backends.map(item => item.ripIds);
        const backendRipIds: string[] = backendRipArray.flat();
        for (const realServer of layer7RealServers) {
            if (!backendRipIds.includes(realServer.id)) {
                hangingRealServerIds.push(realServer.id);
            }
        }

        return hangingRealServerIds;
    }

    /**
     * Get backends with no ACLs rules
     * @private
     */
    private getHangingBackends(): any {
        let aclsIds: any[] = [];
        let redirectLocations: any[] = [];
        this.hangingBackends = [];

        // Get the all the ACL rule ids from the frontends.
        for (const frontend of this.frontends) {
            aclsIds = aclsIds.concat(frontend.aclIds);
        }

        // Match only rules for resolved frontends.
        for (const aclRule of this.aclRules) {
            if (aclsIds.includes(aclRule.id)) {
                redirectLocations = redirectLocations.concat(aclRule.redirectLocation);
            }
        }

        // Return backends that don't have associated frontends.
        for (const backend of this.backends) {
            if (!redirectLocations.includes(backend.id)) {
                this.hangingBackends.push(backend);
            }
        }
    }

    /**
     * Populate table rows with frontends.
     * @private
     */
    private populateTablesRows(): void {
        this.tableRows = [];

        for (const frontend of this.frontends) {
            const fip: FipModel = this.getFIPByID(frontend.fipId);
            const ports: any = ManageServicesComponent.formatPortsForTable(frontend.ports);
            const mode: string = frontend.mode === 'tcpudp' ? 'TCP + UDP' : frontend.mode.toUpperCase();

            this.tableRows.push({
                sslEnable: frontend.SSLEnable,
                id: frontend.id,
                mode,
                serviceName: frontend.name,
                ip: fip ? fip.ip : 'N/A', // Floating IP
                ports: ports,
                serviceType: frontend.type,
                // @ts-ignore
                templateType: frontend.templateType,
                // @ts-ignore
                templateTypeFriendlyName: frontend.templateTypeFriendlyName, // TODO to be updated when layer 4 and GSLB is added
                aclIds: frontend.aclIds,
                status: this.getFriendlyFrontendStatus(frontend)
            });
        }
    }

    /**
     * Get floating IP model object by its ID.
     * @param fipId
     * @private
     */
    private getFIPByID(fipId: string): FipModel {
        for (const fip of this.floatingIPs) {
            if (fip.id === fipId) {
                return fip;
            }
        }

        return null;
    }

    /**
     * Get friendly status to display.
     * @param frontend
     * @private
     */
    private getFriendlyFrontendStatus(frontend: any): string {
        if (this.controllerState?.hasOwnProperty('status')) {
            const statusDisplayName: string = this.utilsService.getStatusDisplayName(this.getFrontendStatus(frontend));

            return this.utilsService.firstLetterUppercase(statusDisplayName);
        } else {
            return 'N/A';
        }
    }

    /**
     * Get the status of a frontend.
     * @param row
     * @private
     */
    private getFrontendStatus(row: any): string | undefined {
        const controllerStatus: any = this.utilsService.getObjectValue(this.controllerState, 'status');

        if (!controllerStatus?.hasOwnProperty(row.id)) {
            return row['templateType'] === 'layer4' ? 'N/A' : 'pending';
        } else {
            return controllerStatus[row.id];
        }
    }

    /**
     * Subscribe to modal state changes.
     * @private
     */
    private setUpModalSubscriber(): void {
        this.modalService.closeParentModalEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                modal => {
                    // Destroy our modal component with reference.
                    if (modal.modalId === 'hangingBackendsModal') {
                        this.modalComponentReference.destroy();
                        this.loadingService.loadingButtonEmitter.next(false);
                    }
                });
    }

    /**
     * Setup socket listeners.
     * @private
     */
    private setupSocketListeners(): void {
        this.widgetService.haDataEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (response: any): void => {
                    this.processWebsocketData(response);
                }
            });

        this.widgetService.sendSubscriptionConfirmEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (service): void => {
                    if (service === 'haproxy') {
                        this.widgetService.receiveSubscriptionConfirmEmitter.next({
                            service: 'haproxy',
                            event: 'stats'
                        });
                    }
                }
            });
    }

    /**
     * Process data from websockets.
     * @param response
     * @private
     */
    private processWebsocketData(response: any): void {
        if (JSON.stringify(this.storedFrontendStatus) !== JSON.stringify(response['frontends'])) {
            const frontends: any = response['frontends'];
            this.storedFrontendStatus = frontends;
            // Create our array if it does not exist.
            // Note: Create array if not exist is expensive for this task.
            this.utilsService.createArrayIfNotExist(this.controllerState, 'status');

            for (const frontend of frontends) {
                const status: string = this.utilsService.getStatusDisplayName(frontend.status);

                this.utilsService.createArrayIfNotExist(this.controllerState['status'], frontend.id);
                this.controllerState['status'][frontend.id] = status;
            }

            // On first pass run search temp rows to filter any query parameters.
            if (this.firstPassSocketData) {
                this.populateTablesRows();
                this.firstPassSocketData = false;
            }
        }
    }

    /**
     * Get the backend for the frontend and navigate to the stick table page.
     * @param id
     * @private
     */
    private navigateToBackendStickTable(id: string): void {
        this.layer7ApiService.getBackendsByFrontendId(id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: L7BackendServiceModel[]): void => {
                    this.router.navigate(
                        [this.navigationService.getEnduranceUrl(
                            `/cluster-configuration/services/backends/stick-table/${data[0].id}/${id}`)]
                    ).then().catch();
                }
            });
    }

    /**
     * Setup subscribers for the page.
     * @private
     */
    private setupSubscribers(): void {
        this.subscribeToQueryParams();
        this.lockServiceSubscriber();
    }

    /**
     * Subscribe to inbound search query.
     * @private
     */
    private subscribeToQueryParams(): void {
        const params: any = this.utilsService.getObjectValue(this.activatedRoute, 'queryParams.value');
        if (params?.sFilter) {
            this.searchTerm = params.sFilter;
            this.navigationService.removeParamsFromUrl();
        } else {
            this.searchTerm = '';
        }

        if (params?.modal === 'true') {
            this.createNewService();
        }
    }

    /**
     * Subscribe to the lock service emitter.
     * @private
     */
    private lockServiceSubscriber(): void {
        this.lockService.locksEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: LockModel): void => {
                    if (data.hasOwnProperty('transactionLocked')) {
                        this.transactionActive = data.transactionLocked;
                    }
                },
                error: (error: Error): void => {
                    this.logService.error('ManageServices:lockServiceSubscriber', 'Unable to subscribe to the lock service', error);
                }
            });
    }

    /**
     * Confirm deletion of multiple IPs.
     * @private
     */
    private confirmDeleteMultipleFrontends(): void {
        const selectedRows: any[] = this.selectedTableRows;
        const message: string = selectedRows.length > 1
            ? `Are you sure you want to delete ${selectedRows.length} frontends?`
            : `Are you sure you want to delete frontend '${selectedRows[0].serviceName}' ?`;
        const confirmationObject: any = {
            title: 'manage_services_delete_multiple_frontends',
            message: message,
            callBack: this.deleteMultipleFrontendsTransaction.bind(this, selectedRows),
            options: { translateText: false }
        };
        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Delete multiple transaction.
     * @param selectedRows
     * @private
     */
    private deleteMultipleFrontendsTransaction(selectedRows: L7VirtualServiceModel[]): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.notifyService.muteLocalNotifications();
            this.transactionService.startTransaction()
                .pipe(
                    concatMap(() => this.deleteMultipleFrontends(selectedRows)),
                    concatMap(() => this.commitTransaction()),
                    takeUntil(this.destroyed$)
                )
                .subscribe({
                    next: (): void => {
                        this.notifyService.unmuteLocalNotifications();
                        this.notifyService.success('All selected frontend have been deleted.');
                        this.progressMessage = '';
                        this.modalService.close('serviceProgressModal');
                        this.setUpPageData();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error(
                            'ManageServices:deleteMultipleFrontendsTransaction',
                            'Error with the transaction',
                            error.message
                        );
                        this.transactionService.rollbackTransaction();
                        observer.error(error);
                    }
                });
        });
    }

    /**
     * Delete multiple IPs.
     * @param selectedRows
     * @private
     */
    private deleteMultipleFrontends(selectedRows: L7VirtualServiceModel[]): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const translation: string = this.languageService.translate('manage_services_deleting');
            const ofText: string = this.languageService.translate('of');
            let index = 1;
            if (selectedRows.length > 1) {
                this.modalService.openModalInParent('serviceProgressModal');
                this.progressMessage = `${translation} ${index} ${ofText} ${selectedRows.length}`;
            }
            const dataToSubmit: Observable<L7VirtualServiceModel> = from(selectedRows || []);
            const call: Observable<any> = dataToSubmit.pipe(
                mergeMap(
                    (row: L7VirtualServiceModel) => this.deleteFrontendHelper(row)
                        .pipe(catchError(error => {
                            this.notifyService.unmuteLocalNotifications();

                            return of(error);
                        }))
                ),
                takeUntil(this.destroyed$)
            );
            call.subscribe({
                next: (response: any): void => {
                    if (response.status === 'fail') {
                        this.updateNotification(response.error.errors);
                    }
                    if (selectedRows.length > 1) {
                        this.progressMessage = `${translation} ${index} ${ofText} ${selectedRows.length}`;
                    }
                    index++;
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                },
                complete: (): void => {
                    observer.next();
                    observer.complete();
                }
            });
        });
    }

    /**
     * Delete frontend helper.
     * @param row
     * @private
     */
    private deleteFrontendHelper(row: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const apiService: any = row.templateType === 'layer4' ? this.layer4ApiService : this.layer7ApiService;
            apiService.deleteFrontend(row.id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (response: any): void => {
                        observer.next(response);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Helper function to update the current notification with more details. e.g. ip address.
     * @param errors
     * @private
     */
    private updateNotification(errors: ErrorsResponseModel): void {
        const message: any = errors[0]?.message;
        const value: any = errors[0]?.value;
        if (message && value) {
            const notification: NotificationModel = this.notifyService.getNotificationsById(`haproxy-frontends-${value}-delete`);
            const frontend: L7VirtualServiceModel = this.frontends.find(item => item.id === value);
            notification.message = `${frontend.name} ${message}`;
        }
    }

    /**
     * Commit transaction.
     * @private
     */
    private commitTransaction(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.transactionService.commitTransaction()
                .subscribe({
                    next: (): void => {
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference: any = {
            'manage-services-delete-service': [{ path: '/haproxy/frontends/:id', methods: ['DELETE'] }],
            'manage-services-drain-service': [{ path: '/haproxy/frontends/:id/drain', methods: ['PUT'] }],
            'manage-services-duplicate-service': this.getDuplicateServicePaths(),
            'manage-services-edit-service': this.getEditServicePaths(),
            'manage-services-edit-ssl-service': this.getSSLServicePaths(),
            'manage-services-halt-service': [{ path: '/haproxy/frontends/:id/halt', methods: ['PUT'] }],
            'manage-services-hanging-reals': [
                { path: '/ipvsDirector/rips', methods: ['GET'] },
                { path: '/haproxy/rips', methods: ['GET'] }
            ],
            'manage-services-hanging-backends': [
                { path: '/ipvsDirector/backends', methods: ['GET'] },
                { path: '/haproxy/backends', methods: ['GET'] }
            ],
            'manage-services-new-service': this.getNewServicePaths(),
            'manage-services-online-service': [{ path: '/haproxy/frontends/:id/online', methods: ['PUT'] }],
            'manage-services-restart-haproxy': [{ path: '/services/:service/restart', methods: ['PUT'] }],
            'manage-services-service-details': this.getServiceDetailsPaths(),
            'manage-services-stick-tables': [{ path: '/haproxy/sticktable/:backend/:start/:end', methods: ['GET'] }]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }

    /**
     * Set the select all button.
     * @private
     */
    private setSelectAllActions(): void {
        this.multiSelectActions = [
            {
                buttonText: 'manage_services_delete_selected',
                buttonAction: this.confirmDeleteMultipleFrontends.bind(this),
                buttonDisabled: !this.permissionsLookup['manage-services-delete-service']
            }
        ];
    }

    /**
     * New service paths
     * @private
     */
    private getNewServicePaths(): any[] {
        return [
            { path: '/fallback', methods: ['GET'] },
            { path: '/fips', methods: ['GET', 'POST'] },
            { path: '/haproxy/acls', methods: ['GET', 'POST'] },
            { path: '/haproxy/backends', methods: ['GET', 'POST'] },
            { path: '/haproxy/frontends', methods: ['GET', 'POST'] },
            { path: '/haproxy/headers', methods: ['POST'] },
            { path: '/haproxy/rips', methods: ['GET', 'POST'] },
            { path: '/healthchecks', methods: ['GET'] },
            { path: '/healthchecks/service/:service', methods: ['GET'] },
            { path: '/interfaces', methods: ['GET'] },
            { path: '/ipgroups', methods: ['GET', 'POST'] },
            { path: '/locks', methods: ['GET'] }
        ];
    }

    /**
     * Get service details paths.
     * @private
     */
    private getServiceDetailsPaths(): any[] {
        return [
            { path: '/haproxy/frontends/:id', methods: ['GET'] },
            { path: '/haproxy/frontends/:id/backends', methods: ['GET'] },
            { path: '/haproxy/backends/:id/rips', methods: ['GET'] },
            { path: '/fips', methods: ['GET'] },
            { path: '/locks', methods: ['GET'] }
        ];
    }

    /**
     * Get edit service paths.
     * @private
     */
    private getEditServicePaths(): any[] {
        return [
            { path: '/fips', methods: ['GET'] },
            { path: '/haproxy/acls', methods: ['GET'] },
            { path: '/haproxy/backends', methods: ['GET'] },
            { path: '/haproxy/frontends', methods: ['GET'] },
            { path: '/haproxy/frontends/:id', methods: ['GET', 'PUT'] },
            { path: '/haproxy/headers/:id', methods: ['GET'] },
            { path: '/haproxy/rips', methods: ['GET'] },
            { path: '/interfaces', methods: ['GET'] },
            { path: '/ipgroups', methods: ['GET'] },
            { path: '/locks', methods: ['GET'] }
        ];
    }

    /**
     * Get permissions for an ssl-enabled frontend.
     * @private
     */
    private getSSLServicePaths(): any[] {
        return [
            { path: '/certificates/certs', methods: ['GET'] },
            { path: '/ciphers', methods: ['GET'] },
            { path: '/ciphers/lists', methods: ['GET'] },
        ];
    }

    /**
     * Get duplicate service paths.
     * @private
     */
    private getDuplicateServicePaths(): any[] {
        return [
            { path: '/fips', methods: ['GET', 'POST'] },
            { path: '/haproxy/acls', methods: ['GET', 'POST'] },
            { path: '/haproxy/backends', methods: ['GET'] },
            { path: '/haproxy/frontends', methods: ['GET', 'POST'] },
            { path: '/haproxy/rips', methods: ['GET'] },
            { path: '/interfaces', methods: ['GET'] },
            { path: '/ipgroups', methods: ['GET'] },
            { path: '/locks', methods: ['GET'] }
        ];
    }
}
